// Filename: src/screens/books/TopFilter.jsx
// Current Date and Time (UTC - YYYY-MM-DD HH:MM:SS formatted): 2025-02-04 19:37:20
// Current User's Login: raghavkunkee

/**
 * TopFilter Component
 * Compact search component with improved layout.
 * Features:
 * - Reduced height design (36px)
 * - Consistent font sizes
 * - Improved spacing
 * - Better alignment
 * 
 * @version 1.2.1
 */

import { useEffect, useState, useRef } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import styled from 'styled-components';
import debounce from 'lodash/debounce';

const TopFilterWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.375rem 0.75rem;
    background: #fff;
    border: 1px solid ${props => props.theme.border.gray};
    border-radius: ${props => props.theme.radius.sizes.standard};
    margin-bottom: 0.75rem;
    height: 36px; // Reduced from 40px
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .pi-book {
        font-size: 0.875rem;
        color: #bc3232;
    }
`;

const BookCount = styled.div`
    font-size: 0.8125rem;
    color: ${props => props.theme.colors.font_dark};
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const FilterContainer = styled.div`
    flex: 1;
    max-width: 500px;
`;

const SearchWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledAutoComplete = styled(AutoComplete)`
    width: 100%;

    .p-autocomplete-input {
        height: 24px; // Reduced from 28px
        border-radius: 4px;
        padding: 0.25rem 1.75rem 0.25rem 0.625rem;
        font-size: 0.8125rem;
        border: 1px solid ${props => props.theme.border.gray};
        width: 100%;
        transition: all 0.2s;
        
        &:focus {
            border-color: #bc3232;
            box-shadow: 0 0 0 2px rgba(188, 50, 50, 0.1);
        }
        
        &::placeholder {
            color: ${props => props.theme.colors.icon_gray};
            font-size: 0.8125rem;
            font-style: italic;
        }
    }

    .p-autocomplete-panel {
        margin-top: 0.25rem;
        border-radius: 4px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border: 1px solid ${props => props.theme.border.gray};
        
        .p-autocomplete-items {
            padding: 0.25rem 0;
        }

        .p-autocomplete-item-group {
            padding: 0.375rem 0.625rem;
            font-size: 0.75rem;
            font-weight: 600;
            background: ${props => props.theme.colors.background};
            color: #bc3232;
            border-bottom: 1px solid ${props => props.theme.border.divider};
            letter-spacing: 0.5px;
            text-transform: uppercase;
        }

        .p-autocomplete-item {
            padding: 0.375rem 0.625rem;
            font-size: 0.8125rem;
            color: ${props => props.theme.colors.font_dark};
            cursor: pointer;
            transition: background-color 0.2s;
            min-height: 24px; // Reduced from 28px
            display: flex;
            align-items: center;

            &:hover {
                background: rgba(188, 50, 50, 0.1);
            }

            &.p-highlight {
                background: rgba(188, 50, 50, 0.15);
                color: #bc3232;
                font-weight: 500;
            }
        }

        .p-autocomplete-empty-message {
            padding: 0.375rem 0.625rem;
            font-size: 0.8125rem;
            color: ${props => props.theme.colors.icon_gray};
            font-style: italic;
        }
    }
`;

const ClearButton = styled(Button)`
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px; // Reduced from 20px
    height: 18px; // Reduced from 20px
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
    color: ${props => props.theme.colors.icon_gray} !important;

    &:hover {
        background: rgba(188, 50, 50, 0.1) !important;
        color: #bc3232 !important;
    }

    &:focus {
        box-shadow: none !important;
    }

    .pi {
        font-size: 0.75rem !important;
    }
`;

const LoadingIndicator = styled.div`
    position: absolute;
    right: 28px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.colors.icon_gray};
    width: 14px; // Reduced from 16px
    height: 14px; // Reduced from 16px
    display: flex;
    align-items: center;
    justify-content: center;

    .pi-spinner {
        font-size: 0.875rem;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

const RefreshButton = styled(Button)`
    width: 24px; // Reduced from 28px
    height: 24px; // Reduced from 28px
    padding: 0 !important;
    background: transparent !important;
    border: 1px solid ${props => props.theme.border.gray} !important;
    color: ${props => props.theme.colors.font_dark} !important;

    &:hover {
        background: rgba(188, 50, 50, 0.1) !important;
        color: #bc3232 !important;
        border-color: #bc3232 !important;
    }

    .pi {
        font-size: 0.8125rem !important;
    }
`;

const TopFilter = ({
    searchKey,
    setSearchKey,
    searchFil,
    setSearchFil,
    setSearchFilter,
    totalBooks = 0,
    onRefresh
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const autoCompleteRef = useRef(null);

    const debouncedSearch = useRef(
        debounce(async (query) => {
            if (!query || query.length < 3) {
                setSuggestions([]);
                return;
            }

            setLoading(true);
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_APIPRODUCTURL}/v1/products/search/autocomplete?q=${query}`,
                    {
                        headers: {
                            Accept: 'application/json;charset=UTF-8'
                        }
                    }
                );

                if (!response.ok) throw new Error('Search failed');

                const data = await response.json();
                const formattedSuggestions = formatSuggestions(data);
                setSuggestions(formattedSuggestions);
            } catch (error) {
                console.error('Search error:', error);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 300)
    ).current;

    const formatSuggestions = (data) => {
        if (!data) return [];
        
        const groups = [];

        if (data.TITLES?.content?.length) {
            groups.push({
                label: 'Titles',
                items: data.TITLES.content.map(item => ({
                    group: 'TITLES',
                    label: item.title || '',
                    value: item
                }))
            });
        }

        if (data.AUTHORS?.content?.length) {
            groups.push({
                label: 'Authors',
                items: data.AUTHORS.content.map(item => ({
                    group: 'AUTHORS',
                    label: item.contributor || '',
                    value: item
                }))
            });
        }

        if (data.PUBLISHER?.content?.length) {
            groups.push({
                label: 'Publishers',
                items: data.PUBLISHER.content.map(item => ({
                    group: 'PUBLISHERS',
                    label: item.publisher || '',
                    value: item
                }))
            });
        }

        if (data.IDENTITIES?.content?.length) {
            groups.push({
                label: 'ISBNs',
                items: data.IDENTITIES.content.map(item => ({
                    group: 'ISBNs',
                    label: item.identifier?.id || '',
                    value: item
                }))
            });
        }

        return groups;
    };

    const handleSearch = (event) => {
        const query = event.target.value || '';
        setSearchKey(query);
        setSearchFil(!!query);
        
        if (query.length >= 3) {
            debouncedSearch(query);
        } else {
            setSuggestions([]);
        }
    };

    const handleSelect = (event) => {
        if (event && event.value) {
            const selectedItem = event.value;
            setSearchFilter([selectedItem]);
            setSearchKey(selectedItem.label || '');
        }
    };

    const handleClear = () => {
        setSearchKey('');
        setSearchFil(false);
        setSearchFilter([]);
        setSuggestions([]);
        autoCompleteRef.current?.focus();
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <TopFilterWrapper>
            <LeftSection>
                <BookCount>
                    <i className="pi pi-book" />
                    {totalBooks.toLocaleString()} books found
                </BookCount>
            </LeftSection>

            <FilterContainer>
                <SearchWrapper>
                    <StyledAutoComplete
                        ref={autoCompleteRef}
                        value={searchKey || ''}
                        suggestions={suggestions}
                        completeMethod={handleSearch}
                        onChange={handleSearch}
                        onSelect={handleSelect}
                        placeholder="Search by Title, Author, Publisher or ISBN (min. 3 chars)"
                        delay={300}
                        minLength={3}
                        scrollHeight="400px"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        optionGroupTemplate={item => item && (
                            <div className="group-header">{item.label}</div>
                        )}
                        emptyMessage="No results found"
                    />
                    {loading && (
                        <LoadingIndicator>
                            <i className="pi pi-spinner" />
                        </LoadingIndicator>
                    )}
                    {searchKey && (
                        <ClearButton
                            type="button"
                            icon="pi pi-times"
                            onClick={handleClear}
                            aria-label="Clear search"
                            tooltip="Clear search"
                        />
                    )}
                </SearchWrapper>
            </FilterContainer>

            <RefreshButton
                type="button"
                icon="pi pi-refresh"
                onClick={onRefresh}
                aria-label="Refresh"
                tooltip="Refresh list"
            />
        </TopFilterWrapper>
    );
};

export default TopFilter;